import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';

import {
  IconAlertCircle,
  IconArrowRight,
  IconCalendarStats,
  IconCreditCard,
  IconSailboat,
  IconShieldCheck,
  IconX,
} from '@tabler/icons-react';

import { EnhancedSelect } from '../../EnhancedSelect';
import {
  BillingPeriod,
  DEFAULT_PLAN_BOAT_ID,
  PLAN_BOATS,
  PlanPromptReason,
  TRIAL_DAYS,
  SubscriptionSnapshot,
  changeStripeSubscriptionPlan,
  createStripeCheckoutSession,
  findPlanBoat,
} from '../../services/subscription';
import '../../styles/PlanSelectionModal.css';

interface PlanSelectionModalProps {
  open: boolean;
  reason: PlanPromptReason;
  mode?: 'checkout' | 'change';
  allowClose?: boolean;
  onClose?: () => void;
  onLogout?: () => void;
  onPlanChanged?: (snapshot: SubscriptionSnapshot) => void;
}

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value);

const formatDate = (date: Date): string =>
  new Intl.DateTimeFormat(undefined, { day: '2-digit', month: 'short', year: 'numeric' }).format(
    date,
  );

const boatOptions = PLAN_BOATS.map((boat) => ({
  value: boat.id,
  label: `${boat.name} - ${boat.tier}`,
}));

const billingPeriodOptions: Array<{ value: BillingPeriod; label: string; suffix: string }> = [
  { value: 'day', label: 'Dia', suffix: 'dia' },
  { value: 'month', label: 'Mensual', suffix: 'mes' },
  { value: 'year', label: 'Anual', suffix: 'año' },
];

const copyByReason: Record<PlanPromptReason, { title: string; body: string }> = {
  'new-user': {
    title: 'Elige tu plan para empezar',
    body: 'Empiezas con 14 dias gratis sin meter tarjeta. Stripe se usa para elegir el plan y anadir pago cuando toque.',
  },
  'missing-plan': {
    title: 'Selecciona un plan',
    body: 'SailTrack necesita que selecciones un plan para activar la prueba gratis, sin meter tarjeta hoy.',
  },
  expired: {
    title: 'Tu plan necesita renovarse',
    body: 'Actualiza el metodo de pago en Stripe para recuperar analisis, reportes y datos agregados.',
  },
  'change-plan': {
    title: 'Cambiar plan',
    body: 'Ajusta el barco y el ciclo de facturacion. Stripe aplicara el cambio sobre tu suscripcion actual.',
  },
};

export const PlanSelectionModal: FC<PlanSelectionModalProps> = ({
  open,
  reason,
  mode = 'checkout',
  allowClose = false,
  onClose,
  onLogout,
  onPlanChanged,
}) => {
  const modalRef = useRef<HTMLElement | null>(null);
  const [boatId, setBoatId] = useState(DEFAULT_PLAN_BOAT_ID);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('month');
  const [isStripeLoading, setIsStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState('');

  const selectedBoat = useMemo(() => findPlanBoat(boatId), [boatId]);
  const modalCopy = copyByReason[reason];
  const trialEndDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + TRIAL_DAYS);
    return date;
  }, []);

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const frameId = window.requestAnimationFrame(() => {
      const modal = modalRef.current;
      const initialFocusTarget = modal?.querySelector<HTMLElement>(
        allowClose ? '.plan-modal-icon-button' : '.enhanced-select__control',
      );
      initialFocusTarget?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [allowClose, open]);

  const getFocusableElements = useCallback((): HTMLElement[] => {
    const modal = modalRef.current;
    if (!modal) return [];
    return [
      ...modal.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((element) => element.getAttribute('aria-hidden') !== 'true');
  }, []);

  const handleDialogKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape' && allowClose) {
      event.preventDefault();
      onClose?.();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements.at(-1);
    if (!firstElement || !lastElement) return;

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }, [allowClose, getFocusableElements, onClose]);

  useEffect(() => {
    if (!open) return;

    const modal = modalRef.current;
    if (!modal) return;

    modal.addEventListener('keydown', handleDialogKeyDown);
    return () => {
      modal.removeEventListener('keydown', handleDialogKeyDown);
    };
  }, [handleDialogKeyDown, open]);

  if (!open) return null;

  const selectedPrice = selectedBoat.prices[billingPeriod];
  const selectedBillingPeriod = billingPeriodOptions.find((option) => option.value === billingPeriod);
  const yearlySavings = Math.max(0, selectedBoat.prices.month * 12 - selectedBoat.prices.year);

  const handleStripeCheckout = async () => {
    setIsStripeLoading(true);
    setStripeError('');
    try {
      if (mode === 'change') {
        const nextSnapshot = await changeStripeSubscriptionPlan({
          boatId,
          billingPeriod,
          trialDays: TRIAL_DAYS,
        });
        onPlanChanged?.(nextSnapshot);
        onClose?.();
        return;
      }

      const url = await createStripeCheckoutSession({
        boatId,
        billingPeriod,
        trialDays: TRIAL_DAYS,
      });
      window.location.href = url;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Stripe billing is not available.';
      setStripeError(message);
    } finally {
      setIsStripeLoading(false);
    }
  };

  return (
    <div className="plan-modal-overlay" role="presentation">
      <section
        ref={modalRef}
        className="plan-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-modal-title"
        tabIndex={-1}
      >
        <div className="plan-modal-header">
          <div>
            <span className="plan-modal-kicker">
              <IconShieldCheck size={16} aria-hidden="true" />
              {mode === 'change' ? 'Gestion de plan' : '14 dias de prueba'}
            </span>
            <h2 id="plan-modal-title">{modalCopy.title}</h2>
            <p>{modalCopy.body}</p>
          </div>
          {allowClose && (
            <button
              type="button"
              className="plan-modal-icon-button"
              aria-label="Cerrar seleccion de plan"
              onClick={onClose}
            >
              <IconX size={20} aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="plan-modal-body">
          <div className="plan-modal-panel plan-modal-selector">
            <label htmlFor="plan-boat-select">
              <IconSailboat size={18} aria-hidden="true" />
              Tipo de barco
            </label>
            <EnhancedSelect
              controlId="plan-boat-select"
              ariaLabel="Tipo de barco"
              className="plan-modal-boat-select"
              options={boatOptions}
              placeholder="Buscar barco"
              value={boatId}
              onChange={setBoatId}
              searchable
            />

            <div className="plan-modal-periods" aria-label="Periodo de facturacion">
              {billingPeriodOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={billingPeriod === option.value ? 'is-selected' : ''}
                  onClick={() => setBillingPeriod(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="plan-modal-feature-list">
              <span>Analisis de regatas y sesiones</span>
              <span>Reportes profesionales</span>
              <span>Comparacion de barcos, eventos y archivos</span>
              <span>
                {mode === 'change' ? 'Facturacion gestionada por Stripe' : 'Sin meter tarjeta durante el trial'}
              </span>
            </div>
          </div>

          <div className="plan-modal-panel plan-modal-summary">
            <div className="plan-modal-summary-top">
              <div>
                <span>{selectedBoat.tier}</span>
                <h3>{selectedBoat.name}</h3>
              </div>
              <IconCalendarStats size={26} aria-hidden="true" />
            </div>

            <div className="plan-modal-price">
              <strong>{formatCurrency(selectedPrice)}</strong>
              <span>/{selectedBillingPeriod?.suffix ?? 'mes'}</span>
            </div>

            {billingPeriod === 'year' && yearlySavings > 0 && (
              <p className="plan-modal-saving">Ahorro anual estimado: {formatCurrency(yearlySavings)}</p>
            )}

            <div className="plan-modal-trial">
              <span>{mode === 'change' ? 'Cambio' : 'Hoy'}</span>
              <strong>{mode === 'change' ? 'Stripe' : '0 EUR'}</strong>
              <small>
                {mode === 'change'
                  ? 'El cambio se aplicara con el prorrateo que corresponda en Stripe.'
                  : `Sin tarjeta hoy; podras anadirla en Stripe antes del ${formatDate(trialEndDate)}`}
              </small>
            </div>

            <button
              type="button"
              className="plan-modal-primary"
              onClick={handleStripeCheckout}
              disabled={isStripeLoading}
              aria-describedby={mode === 'checkout' ? 'plan-modal-cta-note' : undefined}
            >
              {mode === 'change' ? (
                <IconCreditCard size={18} aria-hidden="true" />
              ) : (
                <IconShieldCheck size={18} aria-hidden="true" />
              )}
              {isStripeLoading
                ? 'Abriendo Stripe...'
                : mode === 'change'
                  ? 'Actualizar plan'
                  : 'Empezar prueba gratis'}
              {!isStripeLoading && <IconArrowRight size={18} aria-hidden="true" />}
            </button>

            {mode === 'checkout' && (
              <p id="plan-modal-cta-note" className="plan-modal-cta-note">
                Se abre Stripe; no necesitas tarjeta hoy.
              </p>
            )}

            {!allowClose && onLogout && (
              <button
                type="button"
                className="plan-modal-secondary plan-modal-logout"
                onClick={onLogout}
                disabled={isStripeLoading}
              >
                Cerrar sesión
              </button>
            )}

            {stripeError && (
              <p className="plan-modal-error">
                <IconAlertCircle size={16} aria-hidden="true" />
                {stripeError}
              </p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
